import { Flex, Text } from '@chakra-ui/react';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Badge,
  Button,
  ButtonIcon,
  Modal,
  useToast
} from '@morressier/alembic';
import { useEffect, useState } from 'react';
import { Path } from 'react-hook-form';
import { useUpdateJournalSettings } from 'src/api/mutations';
import { FieldLabel } from 'src/components/Field';
import ChakraMultiSelect, { Option } from 'src/components/MultiSelect';
import { JournalConfigForm } from '.';

/**
 * Fixed-choice counterpart of EditJournalConfigFieldList: the value is a
 * selection over `options` rather than a free-text list. A journal that was
 * never configured falls back to the default of every option being selected,
 * whereas an explicitly saved empty selection stays empty.
 */
const EditJournalConfigMultiSelectField = ({
  label,
  data,
  options,
  fieldName,
  journalId
}: {
  label: string;
  data?: string[];
  options: Option[];
  fieldName: Path<JournalConfigForm>;
  journalId: string;
}) => {
  const toast = useToast();
  const updateSettingMutation = useUpdateJournalSettings(journalId);

  const resolveSelection = (values?: string[]) => {
    if (values === undefined) return options.map(option => option.value);

    return values.filter(value =>
      options.some(option => option.value === value)
    );
  };

  const [isOpen, setIsOpen] = useState(false);
  const [selection, setSelection] = useState<string[]>(() =>
    resolveSelection(data)
  );
  const [savedSelection, setSavedSelection] = useState<string[]>(() =>
    resolveSelection(data)
  );

  useEffect(() => {
    setSelection(resolveSelection(data));
    setSavedSelection(resolveSelection(data));
  }, [data]);

  const openModal = () => {
    setSelection(savedSelection);
    setIsOpen(true);
  };

  const onClose = () => setIsOpen(false);

  const save = async () => {
    const parts = fieldName.split('.');
    if (parts.length < 2) return;

    const nestedKey = parts[1] as string;
    // Persist in the canonical option order so downstream consumers (decision
    // templates, form schemas) get a deterministic list.
    const value = options
      .map(option => option.value)
      .filter(optionValue => selection.includes(optionValue));

    const res = await updateSettingMutation.mutateAsync({
      data: {},
      config: {
        [nestedKey]: value
      }
    });

    if (res.isSuccess) {
      toast({
        variant: 'success',
        title: 'Journal Config Edited',
        isClosable: true
      });
      setSavedSelection(value);
      setSelection(value);
      setIsOpen(false);
      return;
    }

    toast({
      variant: 'error',
      title:
        typeof res?.message === 'string'
          ? res?.message
          : 'Something went wrong',
      isClosable: true
    });
  };

  const isLoading = updateSettingMutation?.isLoading;

  return (
    <>
      <Flex direction="column">
        <Flex alignItems="center">
          <FieldLabel label={label} subText="" isRequired={false} />
          <ButtonIcon
            onClick={openModal}
            ariaLabel="edit-config"
            icon={<FontAwesomeIcon icon={faEdit} />}
          />
        </Flex>
        {savedSelection.length ? (
          <Flex gap={2} wrap="wrap">
            {options
              .filter(option => savedSelection.includes(option.value))
              .map(option => (
                <Badge
                  key={option.value}
                  whiteSpace="normal"
                  wordBreak="break-word"
                >
                  {option.label}
                </Badge>
              ))}
          </Flex>
        ) : (
          <Text textStyle="textContext">No value provided</Text>
        )}
      </Flex>

      {isOpen && (
        <Modal
          isOpen={isOpen}
          onClose={onClose}
          width={{ base: 'calc(100vw - 16px)', md: '42rem' } as any}
          closeButtonProps={{
            hidden: true
          }}
        >
          <Flex
            flexDirection="column"
            gap={4}
            padding={{ base: 4, md: 8 }}
            minHeight="28rem"
          >
            <Flex fontSize={32}>
              <FontAwesomeIcon icon={faEdit} />
            </Flex>
            <Text textStyle="headingGroup">Edit {label}</Text>
            <Flex direction="column" gap={2}>
              <ChakraMultiSelect
                options={options}
                value={selection}
                onChange={setSelection}
                placeholder={`Select ${label.toLowerCase()}`}
                variant="interactive"
                showSelectAllOption
                selectAllLabel="Select All"
              />
            </Flex>
            <Flex
              justifyContent="flex-end"
              gap={2}
              alignItems="center"
              marginTop="auto"
            >
              <Button
                variant="secondary"
                onClick={onClose}
                isDisabled={isLoading}
              >
                Exit Without Saving
              </Button>
              <Button variant="primary" onClick={save} isLoading={isLoading}>
                Save Changes
              </Button>
            </Flex>
          </Flex>
        </Modal>
      )}
    </>
  );
};

export default EditJournalConfigMultiSelectField;
